import { ReactNode, ChangeEvent, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from "@mui/material";

import { PAGE_SIZE_OPTIONS, rememberPageSize } from "@/utils/preferredPageSize";

export interface TableColumn<DataType> {
	label: string;
	minWidth?: number;
	width?: number | string;
	format: (row: DataType) => ReactNode;
	align?: "right" | "left" | "center";
}

interface TableComponentProps<
	DataType extends { id: string | number; type?: string } = { id: string | number; type?: string },
> {
	columns: TableColumn<DataType>[];
	data: DataType[];
	isLoading?: boolean;
	page: number;
	limit: number;
	itemCount: number;
	handleChangePage: (event: unknown, page: number) => void;
	handleChangeLimit: (event: ChangeEvent<HTMLInputElement>) => void;
	noDataText?: string;
	allowNavigation?: boolean;
	hidePagination?: boolean;
}

/**
 * MUI's select calls `onChange` only for a size other than the shown one, so a re-pick of the
 * shown size (a shared link opened at 50, then 50 picked) is read off the clicked option.
 */
const rememberClickedPageSize = (event: MouseEvent<HTMLElement>) => {
	const option = (event.target as HTMLElement).closest("[data-value]");

	if (option) {
		rememberPageSize(Number(option.getAttribute("data-value")));
	}
};

export function TableComponent<DataType extends { id: string | number; type?: string }>({
	columns,
	data,
	page,
	limit,
	itemCount,
	handleChangePage,
	handleChangeLimit,
	noDataText,
	allowNavigation,
	hidePagination,
}: TableComponentProps<DataType>) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<>
			<Table stickyHeader aria-label="sticky table">
				<TableHead sx={{ bgcolor: "white" }}>
					<TableRow>
						{columns.map((column, index) => (
							<TableCell
								key={index}
								align={column.align}
								style={{ minWidth: column.minWidth, width: column.width }}
								variant="head"
							>
								{column.label}
							</TableCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{data && data.length > 0 ? (
						data.map((row) => (
							<TableRow
								key={`${row.id}${row.type ?? ""}`}
								sx={{ cursor: allowNavigation ? "pointer" : "" }}
								tabIndex={-1}
								onClick={() => (allowNavigation ? navigate(`${row.id}`) : null)}
							>
								{columns.map((column, index) => (
									<TableCell
										key={index}
										align={column.align}
										style={{
											minWidth: column.minWidth,
											whiteSpace: column.width === "fit-content" ? "nowrap" : "initial",
										}}
									>
										{column.format(row)}
									</TableCell>
								))}
							</TableRow>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length}>{noDataText ?? t("basics.noData")}</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>

			{data && data.length > 0 && !hidePagination && (
				<TablePagination
					component="div"
					count={itemCount ?? 0}
					page={page - 1}
					rowsPerPage={limit}
					rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
					slotProps={{ select: { MenuProps: { onClick: rememberClickedPageSize } } }}
					onPageChange={handleChangePage}
					onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => {
						rememberPageSize(Number(event.target.value));
						handleChangeLimit(event);
					}}
				/>
			)}
		</>
	);
}
